import classNames from 'classnames';
import { QueryClient, QueryClientProvider, useMutation } from '@tanstack/react-query';
import { htmlSanitize, uuid } from '@loyalty/shared-common';
import { renderToString } from 'react-dom/server';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ClickEventApiClient } from './api/click-event-api-client';
import { GenericComponent } from '../../components-tree';
import { LinkSkeleton } from './LinkSkeleton';
import { ReactBootstrap } from '../../ReactBootstrap';
import { TranslationProvider } from '../TranslationProvider/TranslationProvider';
import { WidgetProps } from '../render-component';

export interface LinkAttributes {
  target: string;
  'data-should-trigger-event': boolean;
  'data-button-id': string;
  className: string;
}

export interface LinkI18nAttributes {
  href: string;
  label: string;
  content: string;
}

export type LinkComponent = GenericComponent<LinkAttributes, LinkI18nAttributes>;
type LinkBackendProps = WidgetProps<LinkComponent>;

interface LinkFrontendProps {
  id: string;
  locale: string;
  programId: string;
  attributes: LinkComponent['attributes'];
  i18nAttributes: LinkComponent['i18nAttributes'];
}

function serializeLinkComponentProps(props: LinkBackendProps): LinkFrontendProps {
  const { state, options, context } = props;
  const { programId } = state;
  const { locale } = context.i18n;

  return {
    id: options.id || options.attributes.id || uuid(),
    locale,
    programId,
    attributes: options.attributes,
    i18nAttributes: options.i18nAttributes,
  };
}

export function LinkRenderer(props: LinkBackendProps, document?: Document) {
  const reactProps = serializeLinkComponentProps(props);
  // FIXME: This Renderer is also used for buttons, need to pass componentType accordingly
  return renderToString(
    <ReactBootstrap
      component="LinkComponent"
      props={reactProps}
      document={document}
      componentType="custom-link"
    />,
  );
}

export function LinkComponent(props: LinkFrontendProps) {
  const queryClient = new QueryClient();

  return (
    <TranslationProvider loader={<LinkSkeleton />}>
      <QueryClientProvider client={queryClient}>
        <Link {...props} />
      </QueryClientProvider>
    </TranslationProvider>
  );
}

function Link(props: LinkFrontendProps) {
  const { t } = useTranslation('participant');
  const i18n = props.i18nAttributes?.[props.locale];
  const apiClient = useMemo(() => new ClickEventApiClient(), []);

  const linkText =
    i18n?.label ||
    htmlSanitize(i18n?.content, { allowedTags: [] }).trim() ||
    t('editor.text.default');

  const shouldTriggerEvent =
    props.attributes['data-should-trigger-event'] && !!props.attributes['data-button-id'];

  const clickEventMutation = useMutation({
    mutationFn: () => apiClient.clickEvent(props.programId, props.attributes['data-button-id']),
  });

  // Generate accessibility label
  const getAccessibilityLabel = () => {
    const accessibilityIndicators: string[] = [];

    if (props.attributes.target === '_blank') {
      accessibilityIndicators.push(t('aria.link.opens-new-tab'));
    }

    if (shouldTriggerEvent) {
      accessibilityIndicators.push(t('aria.link.triggers-event'));
    }

    if (accessibilityIndicators.length > 0) {
      return `${linkText}, ${accessibilityIndicators.join(', ')}`;
    }

    return linkText;
  };

  return (
    <a
      href={i18n?.href || '#'}
      target={props.attributes.target}
      className={classNames(props.attributes.class, props.attributes.className)}
      aria-label={getAccessibilityLabel()}
      onClick={() => {
        if (shouldTriggerEvent) {
          clickEventMutation.mutate();
        }
      }}
    >
      {linkText}
    </a>
  );
}
